import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
    BehaviorSubject,
    Observable,
    catchError,
    filter,
    finalize,
    map,
    switchMap,
    take,
    throwError,
} from "rxjs";
import { TokenService } from "./token.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { ApiResponse } from "../models/api-response.model";
import { LoginInput, RefreshTokenDto, TokenResultDto, UserDto } from "../models/user-dto";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  baseUrl = "User";
  isUserLoggedIn: boolean = false;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private isRefreshing = false; 
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  isAuthenticated: boolean = false;
  constructor(
    private http: HttpClient,
    private router: Router,
    private readonly _tokenService: TokenService,
    private jwtHelper: JwtHelperService
  ) {}
  private currentUserSubject: BehaviorSubject<UserDto>;

  login(loginInput: LoginInput): Observable<ApiResponse<UserDto>> {
    return this.http.post<ApiResponse<UserDto>>(this.baseUrl + "/userLogin", loginInput)
      .pipe(
        map((res: ApiResponse<UserDto>) => {
          if (res != null) {
            //this.currentUserSubject.next(res.value)
            localStorage.setItem("currentAppUser", JSON.stringify(res.value));
          }
          return res;
        })
      );
  }

  refreshToken(): Observable<any> {
    let req: RefreshTokenDto = {
      accessToken: this._tokenService.getRefreshToken(),
      userId: this.getCurrentUserNameFromLocalStorage(),
    };
    if (!this.isRefreshing) {
      // Start the refresh process
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.http
        .post<ApiResponse<TokenResultDto>>("Account/RefreshToken", req)
        .pipe(
          map((res: any) => {
            if (res.token) {
              // Update tokens
              this._tokenService.setToken(res.token, new Date(res.expiresAt));
              this._tokenService.setRefreshToken(
                res.refreshToken.token,
                new Date(res.refreshToken.expiresAt)
              );
              localStorage.setItem("currentAppUser",res.token);

              // Notify subscribers with the new token
              this.refreshTokenSubject.next(res.token);

              location.reload();
            }
            return res.token;
          }),
          catchError((error) => {
            console.error("Error during token refresh", error);
            this.refreshTokenSubject.next(null);
            return throwError(error);
          }),
          finalize(() => {
            this.isRefreshing = false; // Reset the flag after refresh completes
          })
        );
    } else {
      // If a refresh is already in progress, wait for it to complete
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null), // Wait until the token is set
        take(1), // Complete after receiving the first value
        switchMap(() => {
          // Proceed with the original call or whatever is next
          return this.http.post<ApiResponse<TokenResultDto>>(
            "Account/RefreshToken",
            req
          );
        })
      );
    }
  }

  getToken() {
    return this._tokenService.getToken();
  }

  logout() {
    this._tokenService.clearToken();
    this._tokenService.clearRefreshToken();
    let selectedLanguage = localStorage.getItem("lang");
    localStorage.clear();
    localStorage.setItem("lang",selectedLanguage);
   // this.router.navigate(["/login"]);
    window.location.href="/login"
  }

  IsTokenExpired(token: string) {
    return this.jwtHelper.isTokenExpired(token);
  }

  isUserAuthenticated(): boolean {
    let userToken = this.getCurrentUserFromLocalStorage();
    if (userToken) {
      if (!this.jwtHelper.isTokenExpired(userToken)) {
        return true;
      }
      else
        this.logout();
    }
    return false;
  }

  getAuthenticatedUser(): any {
    
    if (this.isUserAuthenticated()) {
      let token = this.getCurrentUserFromLocalStorage();
      let decodedToken = this.jwtHelper.decodeToken(token);
      return decodedToken;
    }
    return null;
  }

  IsInRoles(roleCodes: string[]): boolean {
    const _user = this.getAuthenticatedUser();
    if (!_user) return false;

    const user = _user as UserDto;
    const roles = user.roleNames;

    if (Array.isArray(roles)) {
      return roleCodes.some(role => roles.includes(role));
    }

    if (typeof roles === 'string') {
      return roleCodes.includes(roles);
    }

    return false;
  }

  getCurrentUserToken(): any {
    try {
      const _token = localStorage.getItem("currentAppUser");
      return _token;
    } catch (error) {
      //console.error(error);
      return null as any;
    }
  }

  getCurrentUserFromLocalStorage(): any {
    try {
      
      const _token = localStorage.getItem("currentAppUser");
      return _token == "undefined" || _token == undefined ? null : _token;
    } catch (error) {
      return null as any;
    }
  }

  getCurrentUserNameFromLocalStorage(): any {
    try {
      const _token = localStorage.getItem("currentAppUser");
      let decodedToken = this.jwtHelper.decodeToken(_token);
      var userVM = decodedToken as UserDto;

      return userVM.userName;
    } catch (error) {
      //console.error(error);
      return null as any;
    }
  }

  hasAnyRoleOf(roleCodes: any[]): boolean {
    var _user = this.getAuthenticatedUser();
    if (_user) {
      var userVM = _user as UserDto;
      if (userVM && userVM.role && userVM.role.length > 0) {
        const commonItems = roleCodes.filter((item) =>
          userVM.role.includes(item)
        );
        if (commonItems && commonItems.length > 0) {
          return true;
        }
        return false;
      }
    }
    return false;
  }

  hasRole(roleCode: any): boolean {
    try {
      var _user = this.getAuthenticatedUser();
      if (_user) {
        var userVM = _user as UserDto;
        if (userVM && userVM.roleNames && userVM.roleNames.length > 0) {
          var isExist = roleCode == userVM.roleNames;
          if (isExist) {
            return true;
          } else {
            return false;
          }
        }
        return false;
      }
      return false;
    } catch (error) {
      console.error(error);
      return null as any;
    }
  }

  GetCurrentUserName() {
    this.isUserLoggedIn = this.isUserAuthenticated();
    if (this.isUserLoggedIn) {
      let userObj = this.getAuthenticatedUser();
      return userObj.name;
    } else return null;
  }

  GetCurrentUserId() {
    this.isUserLoggedIn = this.isUserAuthenticated();
    if (this.isUserLoggedIn) {
      let userObj = this.getAuthenticatedUser();
      return userObj.uid;
    } else return null;
  }
}
